import { UploadIcon, FileSpreadsheet } from "lucide-react";
import "./modal-upload-excel.scss";

import { FunctionComponent, useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import { sendOrderFile, setFileStatus } from "../../../store/ReceiptsSlice";

import { Modal, Alert } from "../../ui";

export interface ModalUploadExcelProps {
    onClose: () => void;
    onSuccess: (data: any) => void;
}

const VALID_TYPES = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "text/csv",
];

const ModalUploadExcel: FunctionComponent<ModalUploadExcelProps> = ({ onClose }) => {

    const dispatch = useAppDispatch();
    const sendFileStatus = useAppSelector((state) => state.receipts.fileStatus);

    const [file, setFile]     = useState<File | null>(null);
    const [error, setError]   = useState("");
    const [dragOver, setDrag] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const validateAndSet = (selected: File) => {
        if (!VALID_TYPES.includes(selected.type)) {
            setError("Formato invalido. Envie um arquivo .xlsx, .xls ou .csv");
            return;
        }
        setError("");
        setFile(selected);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDrag(false);
        const dropped = e.dataTransfer.files?.[0];
        if (dropped) validateAndSet(dropped);
    };

    const handleSubmit = async () => {
        if (!file) return;
        dispatch(setFileStatus(!sendFileStatus));
        setError("");
        try {
            const formData = new FormData();
            formData.append("file", file);
            dispatch(sendOrderFile(formData));
        } catch (err: any) {
            setError(err?.message || "Erro ao enviar arquivo. Tente novamente.");
            dispatch(setFileStatus(false));
        }
    };

    return (
        <Modal
            open
            onClose={onClose}
            icon={UploadIcon}
            title="Enviar Excel"
            subtitle="selecione um arquivo excel com as ordens"
            footer={
                <button
                    className="btn-pill primary"
                    disabled={sendFileStatus || !file}
                    onClick={handleSubmit}
                >
                    {sendFileStatus ? <div className="loader" /> : "Enviar"}
                </button>
            }
        >
            {error && <Alert variant="error">{error}</Alert>}

            <div
                className={`drop-zone ${dragOver ? "drag-over" : ""} ${file ? "has-file" : ""}`}
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={handleDrop}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={(e) => {
                        const selected = e.target.files?.[0];
                        if (selected) validateAndSet(selected);
                    }}
                    hidden
                />
                {file ? (
                    <div className="file-info">
                        <FileSpreadsheet size={32} />
                        <p>{file.name}</p>
                        <small>{(file.size / 1024).toFixed(1)} KB</small>
                    </div>
                ) : (
                    <div className="placeholder">
                        <UploadIcon size={32} />
                        <p>Arraste o arquivo aqui ou clique para selecionar</p>
                        <small>.xlsx, .xls ou .csv</small>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ModalUploadExcel;
